import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { RouteGroupKey, routeToGroup } from "../../../constants/routesMapper";
import { ORG_SUBSCRIPTION_PLANS } from "../../../constants/billing";
import { useAppStore } from "../../../hooks/useStores";
import { IUsageData, LineChartDefaultValues, UsageType } from "../../../models/usage";
import { convertDateToUtc, getTodayDateRange } from "../../../utils";
import DashboardWidget from "../Widget/DashboardWidget";
import UsageWidgetAPI from "./API/UsageWidgetAPI";
import UsageWidgetTG from "./TG/UsageWidgetTG";
import styles from "./UsageWidget.module.scss";

type Props = {
	className?: string;
};

const defaultUsageData: { [product in RouteGroupKey]: number } = {
	image_generation: 0,
	image_editing: 0,
	video_editing: 0,
	tailored_generation: 0,
	product_shot_editing: 0,
	ads_generation: 0,
	others: 0,
};

const UsageWidget = ({ className }: Props) => {
	const { t } = useTranslation("dashboard", { keyPrefix: "usage" });
	const { accountStore, tgStore, authStore } = useAppStore();
	const { tgModelStore } = tgStore;
	const [groupedUsageData, setGroupedUsageData] = useState<{ [product in RouteGroupKey]: number }>(defaultUsageData);

	const [startDate, endDate] = getTodayDateRange();

	useEffect(() => {
		const fetchUsage = async () => {
			if (authStore.isLoadingOrgSubscriptions) {
				return;
			}

			if (!tgModelStore.modelsListError && !tgModelStore.loadingModelsList && !tgModelStore.modelsList.length) {
				try {
					await tgModelStore.listAllModels();
				} catch (e) {
					// Swallow error, continue execution
				}
			}

			// Check if org subscription is a free plan
			const getHistoricalData = authStore.orgSubscription?.plan_name === ORG_SUBSCRIPTION_PLANS.free.name;

			const usageData: IUsageData[] = await accountStore.loadAllUsageData(
				convertDateToUtc(startDate).toISOString(),
				convertDateToUtc(endDate).toISOString(),
				Object.keys(LineChartDefaultValues[0]).filter((apiRoute) => apiRoute !== "name"),
				Object.values(UsageType),
				tgModelStore.modelsList.map((model) => model.id),
				getHistoricalData,
			);

			const categorySums: { [product in RouteGroupKey]: number } = { ...defaultUsageData };

			const normalizeRoute = (route: string): string | undefined => {
				const sortedPrefixes = Object.keys(routeToGroup).sort((a, b) => b.length - a.length);
				return sortedPrefixes.find((prefix) => route.startsWith(prefix));
			};

			for (const day of usageData) {
				for (const key in day) {
					if (key === "name") continue;

					const normalized = normalizeRoute(key);
					if (normalized) {
						const group = routeToGroup[normalized] as RouteGroupKey;
						categorySums[group] = (categorySums[group] || 0) + (day[key] || 0);
					}
				}
			}

			setGroupedUsageData(categorySums);
		};

		fetchUsage();
	}, [authStore.isLoadingOrgSubscriptions]);

	return (
		<DashboardWidget className={clsx(styles.widget, className)} title={t("title")}>
			<UsageWidgetAPI
				{...{
					groupedUsageData,
					startDate: startDate.toLocaleDateString(),
					endDate: endDate.toLocaleDateString(),
				}}
			/>
			<UsageWidgetTG {...{ groupedUsageData }} />
		</DashboardWidget>
	);
};

export default observer(UsageWidget);
